import { isEmpty } from 'ramda';
import { useCallback, useMemo, useReducer } from 'react';
import { MediaManagerSDKTotalProgress } from '../../media-manager-sdk';
import {
  getAbortableFiles,
  getFailedFiles,
  getInvalidFiles,
  getNotAbortedFiles,
  getUploadedFiles,
  getNetworkErrorResumableFiles,
  getSeverErrorAutomaticResumeFiles,
} from '../../uploadingFile';
import { TUploaderReducerAction, TUploaderReducerState, UploaderAction } from './types';

const initialState: TUploaderReducerState = [];

const uploaderReducer: (state: TUploaderReducerState, action: TUploaderReducerAction) => TUploaderReducerState = (
  state,
  action,
) => {
  const { type, payload } = action;

  switch (type) {
    case UploaderAction.UpdateQueue:
      const { uploadingFiles } = payload;

      return [...uploadingFiles];

    case UploaderAction.UpdateFileProgress: {
      const {
        progress: { currentFileQueueIdx, currentFileProgress },
      } = payload;

      return [
        ...state.slice(0, currentFileQueueIdx),
        { ...currentFileProgress.file },
        ...state.slice(currentFileQueueIdx + 1),
      ];
    }

    case UploaderAction.ClearQueue: {
      return initialState;
    }

    default:
      return state;
  }
};

export const useUploadQueueReducer = (_initialState = initialState) => {
  const [uploadingFiles, dispatch] = useReducer(uploaderReducer, _initialState);

  const setUploadQueue = useCallback(
    (uploadingFiles) => dispatch({ type: UploaderAction.UpdateQueue, payload: { uploadingFiles } }),
    [],
  );

  const updateUploadingProgress = useCallback(
    (progress: MediaManagerSDKTotalProgress) =>
      dispatch({ type: UploaderAction.UpdateFileProgress, payload: { progress } }),
    [],
  );

  const clearQueue = useCallback(() => {
    dispatch({ type: UploaderAction.ClearQueue });
  }, []);

  const {
    filesCount,
    hasUploadingFiles,
    notAbortedFilesCount,
    uploadedFiles,
    invalidFiles,
    failedFiles,
    notAbortedFiles,
    hasFailedFiles,
    hasNetworkErrorResumableFiles,
    hasServerErrorAutomaticResumeFile,
    hasAbortableFiles,
    invalidFilesCount,
    uploadedFilesCount,
    failedFilesCount,
    notFailedFilesCount,
    areAllFilesUploaded,
  } = useMemo(() => {
    const uploadedFiles = getUploadedFiles(uploadingFiles);
    const invalidFiles = getInvalidFiles(uploadingFiles);
    const failedFiles = getFailedFiles(uploadingFiles);
    const notAbortedFiles = getNotAbortedFiles(uploadingFiles);
    const abortableFiles = getAbortableFiles(uploadingFiles);
    const networkErrorResumableFiles = getNetworkErrorResumableFiles(uploadingFiles);
    const serverErrorAutomaticResumeFile = getSeverErrorAutomaticResumeFiles(uploadingFiles);

    const hasUploadingFiles = !isEmpty(uploadingFiles);
    const hasFailedFiles = !isEmpty(failedFiles);
    const hasAbortableFiles = !isEmpty(abortableFiles);
    const hasNetworkErrorResumableFiles = !isEmpty(networkErrorResumableFiles);
    const hasServerErrorAutomaticResumeFile = !isEmpty(serverErrorAutomaticResumeFile);

    const filesCount = uploadingFiles.length;
    const invalidFilesCount = invalidFiles.length;
    const uploadedFilesCount = uploadedFiles.length;
    const notAbortedFilesCount = notAbortedFiles.length;
    const failedFilesCount = failedFiles.length;
    const notFailedFilesCount = notAbortedFilesCount - failedFilesCount;
    const areAllFilesUploaded = uploadedFilesCount === notFailedFilesCount;

    return {
      filesCount,
      hasUploadingFiles,
      notAbortedFilesCount,
      uploadedFiles,
      invalidFiles,
      failedFiles,
      notAbortedFiles,
      hasFailedFiles,
      hasAbortableFiles,
      hasServerErrorAutomaticResumeFile,
      hasNetworkErrorResumableFiles,
      invalidFilesCount,
      uploadedFilesCount,
      failedFilesCount,
      notFailedFilesCount,
      areAllFilesUploaded,
    };
  }, [uploadingFiles]);

  return {
    uploadingFiles,

    setUploadQueue,
    updateUploadingProgress,
    clearQueue,

    filesCount,
    notAbortedFilesCount,
    invalidFilesCount,
    uploadedFilesCount,
    failedFilesCount,
    notFailedFilesCount,

    hasUploadingFiles,
    areAllFilesUploaded,

    uploadedFiles,
    invalidFiles,
    failedFiles,
    notAbortedFiles,
    hasFailedFiles,
    hasAbortableFiles,
    hasServerErrorAutomaticResumeFile,
    hasNetworkErrorResumableFiles,
  };
};
